import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Mail, Info } from "lucide-react";
import api from "../lib/api-client";

export default function VerifyEmail() {
  const { token } = useParams<{ token: string }>();
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [message, setMessage] = useState("");
  const [resendNotice, setResendNotice] = useState(false);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus("error");
        setMessage("Token de verificación no válido");
        return;
      }

      try {
        const response = await api.get(`/auth/verify-email/${token}`);
        setStatus("success");
        setMessage(
          response.data.message ||
            "¡Email verificado correctamente! Ya puedes iniciar sesión."
        );
      } catch (error: unknown) {
        setStatus("error");
        const axiosError = error as {
          response?: { data?: { message?: string } };
        };
        setMessage(
          axiosError.response?.data?.message ||
            "Error al verificar el email. El enlace puede haber expirado."
        );
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {status === "loading" && (
          <>
            <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-primary-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Verificando tu email...
            </h1>
            <p className="text-gray-600">
              Por favor espera un momento mientras verificamos tu dirección de
              correo.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              ¡Email verificado!
            </h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <Link
              to="/login"
              className="inline-flex items-center justify-center w-full py-3 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
            >
              Iniciar sesión
            </Link>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Error de verificación
            </h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <div className="space-y-3">
              <Link
                to="/login"
                className="inline-flex items-center justify-center w-full py-3 px-4 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Ir a iniciar sesión
              </Link>
              <button
                onClick={() => setResendNotice(true)}
                className="inline-flex items-center justify-center w-full py-3 px-4 text-primary-600 font-medium hover:text-primary-700 transition-colors"
              >
                <Mail className="w-4 h-4 mr-2" />
                Reenviar email de verificación
              </button>
              {resendNotice && (
                <div className="flex items-start gap-2 bg-primary-50 border border-primary-200 rounded-lg px-4 py-3 text-left">
                  <Info className="w-4 h-4 text-primary-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-primary-800">
                    Inicia sesión con tu email para solicitar un nuevo enlace de
                    verificación.
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
